import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  FlaskConical, Play, BarChart3, GraduationCap, Eye, MousePointerClick,
  CheckCircle, Trophy, Pause, RotateCcw, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Play, BarChart3, GraduationCap,
};

const ABTestingTab = () => {
  const [experiment, setExperiment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchExperiment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ab/experiments/hero_cta_v1`);
      setExperiment(res.data);
    } catch (err) {
      console.error('Failed to load experiment:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExperiment(); }, []);

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${API}/ab/experiments/hero_cta_v1/status`, { status });
      toast.success(`Experiment ${status}`);
      fetchExperiment();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const resetExperiment = async () => {
    if (!window.confirm('Reset all A/B test data? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/ab/experiments/hero_cta_v1/reset`);
      toast.success('Experiment data reset');
      fetchExperiment();
    } catch {
      toast.error('Failed to reset');
    }
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto" /></div>;
  if (!experiment) return <div className="py-16 text-center text-gray-500">No experiment data found.</div>;

  const variants = experiment.variants || [];
  const maxCompletionRate = Math.max(...variants.map(v => v.completion_rate || 0), 1);
  const isActive = experiment.status === 'active';

  return (
    <div className="space-y-6" data-testid="ab-testing-tab">
      {/* Experiment Header */}
      <div className="bg-white border-2 border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <FlaskConical size={20} className="text-[#002FA7]" />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {experiment.name}
              </h3>
              <span className={`px-2 py-0.5 text-xs font-bold border ${
                isActive ? 'bg-green-100 text-green-700 border-green-300' :
                experiment.status === 'paused' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                'bg-gray-100 text-gray-600 border-gray-300'
              }`}>
                {experiment.status?.toUpperCase()}
              </span>
            </div>
            <p className="text-sm text-gray-500">{experiment.description}</p>
          </div>
          <div className="flex gap-2">
            {isActive ? (
              <Button onClick={() => updateStatus('paused')} variant="outline" className="btn-sharp h-9 text-xs" data-testid="ab-pause-btn">
                <Pause size={12} className="mr-1" /> Pause
              </Button>
            ) : (
              <Button onClick={() => updateStatus('active')} variant="outline" className="btn-sharp h-9 text-xs" data-testid="ab-resume-btn">
                <Play size={12} className="mr-1" /> Resume
              </Button>
            )}
            <Button onClick={resetExperiment} variant="outline" className="btn-sharp h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="ab-reset-btn">
              <RotateCcw size={12} className="mr-1" /> Reset
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-gray-200 p-3 text-center">
            <Eye size={16} className="mx-auto mb-1 text-gray-400" />
            <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{experiment.total_impressions || 0}</div>
            <div className="text-xs text-gray-500">Total Impressions</div>
          </div>
          <div className="border border-gray-200 p-3 text-center">
            <MousePointerClick size={16} className="mx-auto mb-1 text-blue-500" />
            <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{experiment.total_clicks || 0}</div>
            <div className="text-xs text-gray-500">Total Clicks</div>
          </div>
          <div className="border border-gray-200 p-3 text-center">
            <CheckCircle size={16} className="mx-auto mb-1 text-green-500" />
            <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{experiment.total_completions || 0}</div>
            <div className="text-xs text-gray-500">Demo Completions</div>
          </div>
        </div>

        {!experiment.has_enough_data && (
          <div className="mt-4 bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2">
            <Activity size={14} />
            <span>Need at least <strong>100 impressions</strong> for statistically meaningful results. Currently at {experiment.total_impressions || 0}.</span>
          </div>
        )}
      </div>

      {/* Variant Comparison */}
      <div className="bg-white border-2 border-gray-200 p-6">
        <h4 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Variant Performance</h4>
        <div className="space-y-4">
          {variants.map((v) => {
            const VIcon = ICON_MAP[v.icon] || Play;
            const isWinner = experiment.winning_variant === v.id && experiment.has_enough_data;
            return (
              <div key={v.id} className={`border-2 p-4 ${isWinner ? 'border-green-400 bg-green-50' : 'border-gray-200'}`} data-testid={`ab-variant-${v.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center font-bold text-lg ${
                      v.id === 'A' ? 'bg-blue-100 text-blue-700' :
                      v.id === 'B' ? 'bg-purple-100 text-purple-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {v.id}
                    </div>
                    <div>
                      <div className="font-bold text-sm flex items-center gap-2">
                        <VIcon size={14} />
                        "{v.label}"
                        {isWinner && (
                          <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-bold flex items-center gap-1">
                            <Trophy size={10} /> WINNER
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {v.completion_rate || 0}%
                    </div>
                    <div className="text-xs text-gray-400">completion rate</div>
                  </div>
                </div>

                {/* Funnel */}
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Impressions</div>
                    <div className="font-bold">{v.impressions || 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Clicks</div>
                    <div className="font-bold">{v.clicks || 0}</div>
                    <div className="text-xs text-gray-400">{v.click_rate || 0}% CTR</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Completions</div>
                    <div className="font-bold text-green-700">{v.completions || 0}</div>
                    <div className="text-xs text-gray-400">{v.completion_rate || 0}% CVR</div>
                  </div>
                </div>

                {/* Completion Rate Bar */}
                <div className="mt-3 bg-gray-200 h-3 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      isWinner ? 'bg-green-500' :
                      v.id === 'A' ? 'bg-blue-500' :
                      v.id === 'B' ? 'bg-purple-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${maxCompletionRate > 0 ? (v.completion_rate / maxCompletionRate) * 100 : 0}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
        <p className="font-semibold text-gray-700 mb-2">How This Works</p>
        <ul className="space-y-1">
          <li>- Each new visitor to the Home page is randomly assigned one of {variants.length} CTA button variants</li>
          <li>- Their assignment is saved in their browser so they always see the same variant</li>
          <li>- <strong>Impression</strong> = visitor saw the Home page. <strong>Click</strong> = clicked the CTA. <strong>Completion</strong> = finished the demo exam</li>
          <li>- A winner is declared after 100+ total impressions based on highest completion rate</li>
          <li>- When paused, all visitors see the winning variant (or Variant A as default)</li>
        </ul>
      </div>
    </div>
  );
};

export default ABTestingTab;
